/**
 * Custom `parseSearch` / `stringifySearch` implementations for TanStack Router.
 *
 * The defaults (`parseSearchWith(JSON.parse)` / `stringifySearchWith(JSON.stringify)`)
 * serialize arrays as JSON-encoded strings, producing ugly URLs like
 * `?statuses=%5B%22draft%22%2C%22paused%22%5D`.
 *
 * These helpers serialize arrays with bracket notation, which always
 * round-trips to an array on parse, even for a single value. The brackets go
 * out percent-encoded (`?statuses%5B%5D=draft&statuses%5B%5D=paused`) because
 * that is how TanStack Start's server normalization writes them; see
 * {@link encodeQueryComponent}. Both spellings parse.
 */

const ARRAY_SUFFIX = '[]';

// Types a value only when `stringifySearch` writes it back unchanged.
// Anything else (`1.0`, `null`, `"quoted"`, integers past 2^53) stays the
// string it arrived as: the server redirects to the re-serialized URL, so a
// lossy parse would rewrite the value in the address bar.
function tryParseJson(value: string): unknown {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return value;
  }
  if (parsed === null || typeof parsed === 'string') return value;
  return JSON.stringify(parsed) === value ? parsed : value;
}

export function parseSearch(searchStr: string): Record<string, unknown> {
  const trimmed = searchStr.startsWith('?') ? searchStr.slice(1) : searchStr;
  if (!trimmed) return {};

  const params = new URLSearchParams(trimmed);
  const result: Record<string, unknown> = {};
  const processed = new Set<string>();

  for (const rawKey of params.keys()) {
    if (processed.has(rawKey)) continue;
    processed.add(rawKey);

    const values = params.getAll(rawKey);

    if (rawKey.endsWith(ARRAY_SUFFIX)) {
      const key = rawKey.slice(0, -ARRAY_SUFFIX.length);
      result[key] = values.map((v) => tryParseJson(v));
    } else if (values.length > 1) {
      result[rawKey] = values.map((v) => tryParseJson(v));
    } else {
      result[rawKey] = tryParseJson(values[0] ?? '');
    }
  }

  return result;
}

// TanStack Start normalizes request queries through URLSearchParams before
// comparing them to the router's canonical URL. Use the same form encoding
// for keys and the `[]` suffix too: any character it escapes differently,
// such as a space, `~` or `[`, gives the request and the canonical URL
// different spellings, and the redirect never settles.
export function encodeQueryComponent(value: string): string {
  return new URLSearchParams([['value', value]])
    .toString()
    .slice('value='.length);
}

function encodeItem(item: unknown): string {
  if (typeof item === 'string') return encodeQueryComponent(item);
  return encodeQueryComponent(JSON.stringify(item));
}

export function stringifySearch(search: Record<string, unknown>): string {
  const parts: string[] = [];

  for (const [key, value] of Object.entries(search)) {
    if (value === undefined || value === null) continue;

    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      const encodedKey = encodeQueryComponent(`${key}${ARRAY_SUFFIX}`);
      for (const item of value) {
        if (item === undefined || item === null) continue;
        parts.push(`${encodedKey}=${encodeItem(item)}`);
      }
    } else if (typeof value === 'string') {
      parts.push(`${encodeQueryComponent(key)}=${encodeQueryComponent(value)}`);
    } else {
      parts.push(
        `${encodeQueryComponent(key)}=${encodeQueryComponent(JSON.stringify(value))}`,
      );
    }
  }

  return parts.length > 0 ? `?${parts.join('&')}` : '';
}
